<template>
  <div class="dashboard-date-picker">
    <!-- Previous Day Button -->
    <q-btn
      :disable="!canSelectPreviousDay"
      round
      color="primary"
      class="q-mx-xs"
      flat
      icon="chevron_left"
      unelevated
      @click="previousDay"
    >
      <q-tooltip
        class="bg-primary"
        :delay="500"
        :offset="[0, 10]"
      >
        <div class="text-caption text-white">
          {{ tooltipPrevious }}
        </div>
      </q-tooltip>
    </q-btn>

    <q-btn
      outline
      rounded
      unelevated
      :label="qdate.formatDate(selectedDate, 'DD/MM/YYYY')"
      class="q-mx-xs text-bold h-[40px]"
      icon="event"
      color="primary"
    >
      <q-menu
        anchor="bottom right"
        self="top end"
        transition-show="scale"
        transition-hide="scale"
      >
        <q-date
          :model-value="selectedDate"
          minimal
          :options="dateOptions"
          no-unset
          :navigation-min-year-month="formattedMinDate"
          :navigation-max-year-month="formattedMaxDate"
          @update:model-value="updateSelectedDate"
        >
          <div class="row items-center justify-end">
            <q-btn
              v-close-popup
              :label="$q.lang.label.close"
              color="primary"
              flat
            />
          </div>
        </q-date>
      </q-menu>
    </q-btn>

    <!-- Next Day Button -->
    <q-btn
      :disable="!canSelectNextDay"
      round
      color="primary"
      class="q-mx-xs"
      flat
      icon="chevron_right"
      unelevated
      @click="nextDay"
    >
      <q-tooltip
        class="bg-primary"
        :delay="500"
        :offset="[0, 10]"
      >
        <div class="text-caption text-white">
          {{ tooltipNext }}
        </div>
      </q-tooltip>
    </q-btn>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { date as qdate } from 'quasar';

const props = defineProps({
  tooltipNext: {
    type: String,
    required: true
  },
  tooltipPrevious: {
    type: String,
    required: true
  },
  minDate: {
    type: String,
    required: true
  },
  maxDate: {
    type: String,
    required: true
  },
  selectedDate: {
    type: String,
    required: true
  }
});

const emits = defineEmits(['update:selected-date']);

const canSelectPreviousDay = computed(() => {
  return qdate.isBetweenDates(
    qdate.subtractFromDate(props.selectedDate, { days: 1 }),
    props.minDate,
    props.maxDate,
    { inclusiveFrom: true, inclusiveTo: true }
  );
});

const canSelectNextDay = computed(() => {
  return qdate.isBetweenDates(
    qdate.addToDate(props.selectedDate, { days: 1 }),
    props.minDate,
    props.maxDate,
    { inclusiveFrom: true, inclusiveTo: true }
  );
});

const formattedMinDate = computed(() => {
  return qdate.formatDate(props.minDate, 'YYYY/MM');
});

const formattedMaxDate = computed(() => {
  return qdate.formatDate(props.maxDate, 'YYYY/MM');
});

function previousDay() {
  if (canSelectPreviousDay.value) {
    updateSelectedDate(
      qdate.formatDate(qdate.subtractFromDate(props.selectedDate, { days: 1 }), 'YYYY/MM/DD')
    );
  }
}

function nextDay() {
  if (canSelectNextDay.value) {
    updateSelectedDate(
      qdate.formatDate(qdate.addToDate(props.selectedDate, { days: 1 }), 'YYYY/MM/DD')
    );
  }
}

function updateSelectedDate(newDate) {
  emits('update:selected-date', newDate);
}

function dateOptions(dateStr) {
  return qdate.isBetweenDates(dateStr, props.minDate, props.maxDate, { inclusiveFrom: true, inclusiveTo: true });
}

function handleKeyDown(event) {
  if (event.key === 'ArrowLeft') {
    previousDay();
  } else if (event.key === 'ArrowRight') {
    nextDay();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>
